/**
 * 데이터 표 헤더 셀.
 *
 *   - 라벨 영역 클릭 → 정렬 cycle (asc → desc → 해제)
 *   - 정렬 활성: ▲ 또는 ▼ 파란색 표시
 *   - 우측 돋보기 아이콘 → 필터·정렬 팝오버
 *     · 평소엔 숨김 (opacity 0), 헤더 hover 시 표시
 *     · 필터 활성 시 항상 표시 + 파란색
 *
 * 📐 내 표 설정 (excelMode):
 *   - excelMode=true: 헤더 draggable + 좌측 ◀ 우측 ▶ 이동 버튼 + 우측 1px resize 핸들 노출.
 *     정렬·필터 동작은 그대로 — 짧은 클릭=정렬, 드래그=이동 (브라우저 native 분리).
 *   - excelMode=false: 드래그·resize·이동 버튼 비활성 (정렬·필터만).
 *   - widthPx prop 있으면 width style 적용 (minWidth 미적용 → 해상도에 따라 비례 늘어남/줄어듦).
 */

import { useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import type { Column } from './types';
import type { ColStateApi } from './useColState';
import { ColumnFilterPopover } from './ColumnFilterPopover';

interface Props<T> {
  column: Column<T>;
  /** 정렬·필터 적용 전 원본 행 (팝오버에서 고유값 추출 기준) */
  baseRows: T[];
  /**
   * 표의 **모든** 컬럼 — 엑셀식 연쇄 필터에 쓴다 (다른 컬럼 필터를 적용해 목록을 좁히려면
   * 그 컬럼들의 accessor 가 필요하다). 안 넘기면 연쇄 없이 예전처럼 전체 목록이 나온다.
   */
  columns?: Column<T>[];
  colState: ColStateApi;
  /** 📐 내 표 설정 ON 상태. true 면 드래그·resize·이동 버튼 활성. */
  excelMode?: boolean;
  /** excelMode 시 컬럼 이동 콜백. fromKey 를 toKey 의 [before|after] 위치로 옮김. */
  onReorder?: (fromKey: string, toKey: string, position: 'before' | 'after') => void;
  /** excelMode 시 ◀ 버튼 — 한 칸 왼쪽 이동. undefined 또는 첫 컬럼이면 비활성. */
  onMoveLeft?: () => void;
  /** excelMode 시 ▶ 버튼 — 한 칸 오른쪽 이동. undefined 또는 마지막 컬럼이면 비활성. */
  onMoveRight?: () => void;
  /** excelMode 시 너비 조정 콜백 (px). */
  onResize?: (key: string, widthPx: number) => void;
  /** prefs 에 저장된 너비 (px). 없으면 자동. minWidth 미적용 → 해상도 따라 비례 늘어남. */
  widthPx?: number;
}

export function ColumnHeader<T>({
  column,
  baseRows,
  columns,
  colState,
  excelMode = false,
  onReorder,
  onMoveLeft,
  onMoveRight,
  onResize,
  widthPx,
}: Props<T>) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [dropZone, setDropZone] = useState<'before' | 'after' | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const thRef = useRef<HTMLTableCellElement>(null);

  const sortIndex = colState.sortStack.findIndex((s) => s.key === column.key);
  const sortSpec = sortIndex >= 0 ? colState.sortStack[sortIndex] : null;
  const isPrimary = sortIndex === 0;
  const sortDir = sortSpec?.dir ?? null;
  const hasFilter = !!colState.filters[column.key];

  const alignCls =
    column.align === 'right'
      ? 'text-right'
      : column.align === 'center'
        ? 'text-center'
        : 'text-left';

  // ───── Resize (우측 1px 핸들) ─────
  // 드래그 중에는 DOM 직접 조작으로 즉시 피드백, 최종값만 onResize 로 persist.
  function startResize(e: ReactMouseEvent<HTMLDivElement>) {
    if (!excelMode || !onResize) return;
    e.preventDefault();
    e.stopPropagation();
    const th = thRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.getBoundingClientRect().width;
    let lastWidth = startWidth;

    function onMove(ev: MouseEvent) {
      lastWidth = Math.max(50, Math.round(startWidth + (ev.clientX - startX)));
      if (thRef.current) {
        thRef.current.style.width = `${lastWidth}px`;
      }
    }
    function onUp() {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      onResize!(column.key, Math.round(lastWidth));
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
  }

  // ───── Drag & drop (컬럼 이동) ─────
  function handleDragStart(e: React.DragEvent<HTMLTableCellElement>) {
    if (!excelMode || !onReorder) return;
    e.dataTransfer.setData('application/x-col-key', column.key);
    e.dataTransfer.effectAllowed = 'move';
  }
  function handleDragOver(e: React.DragEvent<HTMLTableCellElement>) {
    if (!excelMode || !onReorder) return;
    const from = e.dataTransfer.types.includes('application/x-col-key');
    if (!from) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const isLeft = e.clientX - rect.left < rect.width / 2;
    setDropZone(isLeft ? 'before' : 'after');
  }
  function handleDragLeave() {
    setDropZone(null);
  }
  function handleDrop(e: React.DragEvent<HTMLTableCellElement>) {
    if (!excelMode || !onReorder) return;
    e.preventDefault();
    const from = e.dataTransfer.getData('application/x-col-key');
    if (from && from !== column.key && dropZone) {
      onReorder(from, column.key, dropZone);
    }
    setDropZone(null);
  }

  const dropBeforeCls =
    dropZone === 'before' ? 'shadow-[inset_2px_0_0_0_#2563eb]' : '';
  const dropAfterCls =
    dropZone === 'after' ? 'shadow-[inset_-2px_0_0_0_#2563eb]' : '';

  // 너비 — width 만, minWidth 미적용 → 해상도 따라 비례 늘어남/줄어듦.
  const inlineWidth = widthPx ? { width: `${widthPx}px` } : undefined;

  return (
    <th
      ref={thRef}
      scope="col"
      draggable={excelMode && !!onReorder}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={inlineWidth}
      className={`group relative h-8 px-2 py-0 font-bold text-[13px] text-slate-700 select-none ${alignCls} ${
        column.mobileHidden ? 'hidden md:table-cell' : ''
      } ${column.nowrapHeader ? 'whitespace-nowrap' : ''} ${
        excelMode
          ? 'cursor-grab active:cursor-grabbing bg-blue-50/40 outline-dashed outline-1 outline-blue-200 outline-offset-[-3px]'
          : ''
      } ${dropBeforeCls} ${dropAfterCls}`}
      title={excelMode ? `${column.label} — 끌어서 이동 / 우측 끝 끌어서 너비 조정 / ◀ ▶ 한 칸씩 이동` : undefined}
    >
      <div
        className={`inline-flex items-center gap-0.5 ${
          column.align === 'right'
            ? 'justify-end'
            : column.align === 'center'
              ? 'justify-center'
              : ''
        }`}
      >
        {/* excelMode: 좌측 ◀ 이동 버튼 */}
        {excelMode && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onMoveLeft?.();
            }}
            disabled={!onMoveLeft}
            className="text-[11px] text-slate-500 hover:text-blue-700 disabled:text-slate-300 disabled:cursor-not-allowed px-0.5"
            title="한 칸 왼쪽으로"
            aria-label={`${column.label} 한 칸 왼쪽으로`}
          >
            ◀
          </button>
        )}
        {/* 정렬이 뜻 없는 칸(단추만 있는 칸)은 누를 수 없는 글자로 — 눌러도 안 바뀌는 단추를 만들지 않는다 (v2.149.0) */}
        {column.noSort ? (
          <span>{column.label}</span>
        ) : (
          <button
            type="button"
            onClick={() => colState.toggleSort(column.key)}
            className="hover:text-blue-700"
            title="정렬 (오름→내림→해제)"
          >
            {column.label}
          </button>
        )}
        {sortDir && (
          <span
            className={`text-[11px] ${
              isPrimary ? 'text-blue-600 font-bold' : 'text-slate-400'
            }`}
          >
            {sortDir === 'asc' ? '▲' : '▼'}
            {!isPrimary && <sub className="ml-0.5">{sortIndex + 1}</sub>}
          </span>
        )}
        {/* 정렬 안 된 칸의 화살표는 마우스를 올릴 때만 (v2.317.0 · Salesforce 식) — 「누를 수 있다」만 알린다 */}
        {!sortDir && !column.noSort && (
          <span className="text-[11px] text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true">
            ▲
          </span>
        )}
        {/* 필터·정렬 팝오버 트리거 — 값이 없는 칸에는 아예 안 붙인다 (v2.149.0) */}
        {column.noFilter ? null : (
        <button
          ref={triggerRef}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setPopoverOpen((o) => !o);
          }}
          className={`ml-0.5 px-0.5 transition-opacity ${
            hasFilter
              ? 'opacity-100 text-blue-600'
              : popoverOpen
                ? 'opacity-100 text-slate-600'
                : 'opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-700'
          }`}
          title={hasFilter ? '필터 (적용 중)' : '필터'}
          aria-label={`${column.label} 필터`}
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
        )}
        {/* excelMode: 우측 ▶ 이동 버튼 */}
        {excelMode && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onMoveRight?.();
            }}
            disabled={!onMoveRight}
            className="text-[11px] text-slate-500 hover:text-blue-700 disabled:text-slate-300 disabled:cursor-not-allowed px-0.5"
            title="한 칸 오른쪽으로"
            aria-label={`${column.label} 한 칸 오른쪽으로`}
          >
            ▶
          </button>
        )}
      </div>

      {/* Resize 핸들 — excelMode 일 때만 우측 끝 1px 영역 */}
      {excelMode && onResize && (
        <div
          onMouseDown={startResize}
          className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-400/60 z-10"
          title="끌어서 너비 조정"
        />
      )}

      {popoverOpen && (
        <ColumnFilterPopover
          column={column}
          baseRows={baseRows}
          columns={columns}
          colState={colState}
          anchorRef={triggerRef}
          onClose={() => setPopoverOpen(false)}
        />
      )}
    </th>
  );
}
